import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";

import CustomWidget from "../CustomWidget";
import { hasMetricPlan, metricsFromPlan } from "../../helpers/metricPlan";
import { resolveCardWindow } from "../../helpers/cardWindow";
import useScopedMetrics from "../../hooks/useScopedMetrics";
import useDerivedRevenueMetrics, { isDerivedMetricId, BEAT_YETI_RATE_ID, TECH_REVENUE_PER_HOUR_ID } from "../../hooks/useDerivedRevenueMetrics";
import { yetiByTeamOn, normalizeYetiGoal, normalizeYetiGoals, yetiGoalFor } from "../../helpers/beatYeti";
import useTotalRevenue from "../GoalProgress/useTotalRevenue";
import { rawTargetOf } from "../../helpers/kpiTargetScale";
import { yetiPaceFor, teamSizeOf } from "../CustomWidget/yetiPace";
import CardBusy from "./CardBusy";
import "./cardScope.css";

/** Local copy — importing metricsOf from widgetRegistry creates a cycle
 *  (registry → ScopedCustomWidget → registry). */
const metricsOf = (s) => {
  if (s && Array.isArray(s.metrics) && s.metrics.length) {
    return s.metrics
      .filter((m) => m && m.metricId && String(m.metricId) !== "paired")
      .map((m) => ({ metricId: m.metricId, graph: m.graph || "number", color: m.color || null }));
  }
  if (s && s.metricId && String(s.metricId) !== "paired") {
    return [{ metricId: s.metricId, graph: s.graph || "number", color: null }];
  }
  return [];
};

/**
 * Build-your-own card across one or more workspaces.
 *
 * Cross-workspace pairing is ONLY when the admin saved an explicit metricPlan
 * AND picked 2+ workspaces. Auto-pairing from metric titles used to pull a KPI
 * from the other team, expand the card to both workspaces, and collapse every
 * tile into one "Estimate Won + Closed Rate" at 0%.
 */

const nameKey = (k) => (k?.title || k?.name || "").trim().toLowerCase();
const looseKey = (s) => (s || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "");

/** Find a KPI def, preferring workspaces in `preferWs` so In-House Closed Rate
 *  is not silently swapped for Sales Closed Rate. Skip disabled/draft. */
const findDefAnywhere = (metricId, companyKpis, preferWs = []) => {
  if (!metricId || !companyKpis || typeof companyKpis !== "object") {
    return { def: null, wsId: null };
  }
  const id = String(metricId);
  const prefer = (preferWs || []).map(String).filter(Boolean);
  const allKeys = Object.keys(companyKpis);
  const keys = [...prefer.filter((k) => allKeys.includes(k)), ...allKeys.filter((k) => !prefer.includes(k))];
  const usable = (k) => k && !k.isDraft && !k.disabled && !k.retired;

  for (let i = 0; i < keys.length; i += 1) {
    const wsId = keys[i];
    const defs = companyKpis[wsId] || [];
    const hit = defs.find((k) => usable(k) && String(k._id) === id);
    if (hit) return { def: hit, wsId: String(wsId) };
  }
  const want = id.trim().toLowerCase();
  const wantLoose = looseKey(id);
  if (!want) return { def: null, wsId: null };
  for (let i = 0; i < keys.length; i += 1) {
    const wsId = keys[i];
    const defs = companyKpis[wsId] || [];
    const hit = defs.find((k) => {
      if (!usable(k)) return false;
      const nk = nameKey(k);
      return nk === want || looseKey(nk) === wantLoose;
    });
    if (hit) return { def: hit, wsId: String(wsId) };
  }
  return { def: null, wsId: null };
};

const resolveRowKpi = (row, pool, companyKpis, preferWs = []) => {
  if (!row || !row.metricId) return null;
  const id = String(row.metricId);
  if (id === "paired") return null;

  const byId = (pool || []).find((k) => k && String(k._id) === id);
  if (byId) {
    return { ...byId, _id: id, _seriesIds: [String(byId._id)] };
  }

  const { def } = findDefAnywhere(id, companyKpis, preferWs);
  if (def) {
    const nk = nameKey(def);
    const lk = looseKey(nk);
    const byName = (pool || []).find((k) => {
      const pk = nameKey(k);
      return pk === nk || looseKey(pk) === lk;
    });
    const seriesIds = [String(def._id)];
    if (byName) seriesIds.push(String(byName._id));
    if (byName) return { ...byName, _id: id, _seriesIds: [...new Set(seriesIds)] };
    const daily = Number(def.target) || Number(def.minimum) || 0;
    return {
      ...def,
      _id: id,
      _seriesIds: [String(def._id)],
      amount: Number(def.amount) || 0,
      _dailyTarget: daily,
      target: daily,
      points: Number(def.points) || 0,
    };
  }

  const want = id.trim().toLowerCase();
  const wantLoose = looseKey(id);
  const byTitle = (pool || []).find((k) => {
    const pk = nameKey(k);
    return pk === want || looseKey(pk) === wantLoose;
  });
  if (byTitle) return { ...byTitle, _id: id, _seriesIds: [String(byTitle._id)] };
  return null;
};

/* Company-wide, workspace-owned, ungamified rollups (Total Revenue, Total
   Scheduled Revenue) are deliberately excluded from every scoped tile by
   useScopedMetrics itself (see its own top-of-file comment: "kept out of
   every scoped tile ... custom cards") — a Build Your Own row picking one of
   these can never resolve through the normal pool/name-match path above; it
   always falls through to a synthetic $0. They need the SAME dedicated
   per-job-summed fetch Payroll vs Revenue / Goal Progress already use
   (useTotalRevenue), read from the KPI's own home workspace — not whatever
   workspace(s) this card happens to be scoped to. */
const ROLLUP_KPI_TYPES = new Set(["total_revenue", "scheduled_revenue"]);
const rollupTypeOf = (def) => def?.type || def?.model?.type;
const isRollupKpi = (def) => ROLLUP_KPI_TYPES.has(rollupTypeOf(def));
/* Build Your Own caps a card at this many metric rows (widgetRegistry's
   MAX_METRICS) — useTotalRevenue is called a fixed number of times (React's
   rule against conditional hooks), so this is the ceiling regardless of how
   many rows actually end up being rollup rows. */
const MAX_ROLLUP_SLOTS = 6;

const ScopedCustomWidget = ({ ctx, s, widget }) => {
  const companyKpis = useSelector((state) => state.kpiReducer.companyKpis);
  const settingsWs = (s.workspaceIds || []).filter(Boolean).map(String);
  const boardWs = ctx.boardWs && ctx.boardWs !== "all" ? String(ctx.boardWs) : null;
  const isAdmin = !!widget.isRealAdmin;
  const inherited = (ctx.inheritWorkspaceIds || []).filter(Boolean).map(String);
  const planRaw = hasMetricPlan(s.metricPlan) ? s.metricPlan : null;
  const planWs = planRaw
    ? [...new Set((planRaw.cols || []).flatMap((c) => Object.keys(c?.sources || {})).map(String))]
    : [];
  const metricRows = metricsFromPlan(planRaw) || metricsOf(s);
  /* "Derived" rows (Average Daily Revenue, Technicians' Revenue per Hour) are
     not real KPI documents — resolveRowKpi below can never find one, so they
     are pulled out here and answered separately (see the merge right before
     the goal-stamping step) instead of running the usual by-id / by-name KPI
     resolution the rest of this component does. */
  const derivedRows = metricRows.filter((r) => isDerivedMetricId(r.metricId));
  /* "Rollup" rows — a row whose title name-matches a REAL Total Revenue /
     Total Scheduled Revenue KPI. Pulled out for the same reason as derived
     rows: useScopedMetrics deliberately never carries these (see
     isRollupKpi's own comment), so leaving them in realMetricRows would just
     resolve to a synthetic $0 every time. */
  const rollupCandidates = metricRows.filter((r) => !isDerivedMetricId(r.metricId));
  const rollupLookup = useMemo(
    () => rollupCandidates.map((r) => {
      const { def, wsId } = findDefAnywhere(r.metricId, companyKpis, []);
      return isRollupKpi(def) ? { row: r, workspaceId: wsId, kpiId: String(def._id) } : null;
    }),
    /* The whole row (not just metricId) — it carries `graph`/`color`, which
       the admin can change without the metricId itself changing. Keying this
       memo on metricId alone meant switching graph type on a rollup row
       (Total Scheduled Revenue, Total Revenue) silently kept serving the
       stale row.graph from before the switch — the chart never updated,
       live preview or saved, because `row: r` here was the cached one, not
       the current one. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(rollupCandidates), companyKpis],
  );
  const rollupRows = rollupLookup.filter(Boolean);
  const rollupRowIds = new Set(rollupRows.map((r) => String(r.row.metricId)));
  const realMetricRows = metricRows.filter(
    (r) => !isDerivedMetricId(r.metricId) && !rollupRowIds.has(String(r.metricId)),
  );

  /* Explicit cross-workspace plan only. Never invent a pairing from titles. */
  let effectiveWs = settingsWs;
  if (effectiveWs.length < 2 && planWs.length >= 2) effectiveWs = planWs;
  const multiWs = effectiveWs.length >= 2;
  const plan = multiWs && planRaw ? planRaw : null;

  /* Stay on the workspaces the admin picked. Do NOT expand to every workspace
     that happens to own a same-named KPI — that was the Inside Sales zero. */
  let preferred = inherited;
  if (effectiveWs.length) preferred = effectiveWs;
  else if (inherited.length) preferred = inherited;
  else if (ctx.workspaceId) preferred = [String(ctx.workspaceId)];

  /* A card aimed at 2+ workspaces is always a team view — every viewer should
     see the pooled number, not just admins. Admins still get scoped reads for
     single-workspace / board-filter picks. */
  const teamScoped = multiWs || !!plan;
  const adminScoped = isAdmin && (
    boardWs != null
    || (effectiveWs.length <= 1 && preferred.length > 0)
    || inherited.length >= 2
  );
  /* Match/Follow dashboard → navbar; pinned PeriodField / ScopeWindow → pin. */
  const { window: displayWin, custom: displayCustom } = resolveCardWindow(s, {
    window: ctx.boardWindow || ctx.periodName || null,
    custom: ctx.boardCustom || null,
  });
  /* Custom / quarter / YTD (etc.) cannot use month-bucket ctx.kpis — that was
     Average Ticket on Yeti (scoped window) disagreeing with the same tile on
     the workspace board (still This Month under the hood). Force the scoped
     daily-score path whenever the resolved window is not plain This Month. */
  const windowNeedsScoped = !!(displayWin && displayWin !== "month" && displayWin !== "follow");
  const scoped = teamScoped || adminScoped || windowNeedsScoped;

  let reading = [];
  if (scoped) {
    /* Saved workspaceIds / plan sources beat the board navbar filter. */
    if (teamScoped && effectiveWs.length >= 2) reading = effectiveWs;
    else if (boardWs) reading = [boardWs];
    else if (preferred.length) reading = preferred;
    else if (ctx.workspaceId) reading = [String(ctx.workspaceId)];
  } else if (preferred.length) {
    /* Still need daily scores for ▲/▼ on number tiles when amounts come from ctx. */
    reading = preferred;
  } else if (ctx.workspaceId) {
    reading = [String(ctx.workspaceId)];
  }

  const data = useScopedMetrics(reading, {
    window: displayWin,
    custom: displayCustom,
    show: s.avgMode === "total" ? "total" : "avg",
    plan: plan || null,
  });
  /* A step bonus can be paid on a derived metric the card doesn't itself show
     (a revenue card that pays on Beat the Yeti rate). It still has to be
     computed, but it gets no tile of its own — see the merge after the
     single-kpi remap below. */
  const bonusOf = s.bonus && typeof s.bonus.ofMetric === "string" ? s.bonus.ofMetric : null;
  const bonusOnlyDerivedId = bonusOf && isDerivedMetricId(bonusOf)
    && !derivedRows.some((r) => String(r.metricId) === bonusOf)
    ? bonusOf
    : null;
  const derivedIds = [...derivedRows.map((r) => r.metricId), ...(bonusOnlyDerivedId ? [bonusOnlyDerivedId] : [])];
  /* `reading` so Beat the Yeti rate asks about the roster this card is
     pointed at; the revenue-derived pair ignore it (company-wide by
     design — see the hook). */
  /* Beat the Yeti rate's extras (see helpers/beatYeti): a row per tracked
     workspace, an overall bar under the number, and a goal notch. */
  const yetiByTeam = yetiByTeamOn(s);
  const yetiBar = s.yetiBar === true;
  const yetiGoal = normalizeYetiGoal(s.yetiGoal);
  const yetiGoals = normalizeYetiGoals(s.yetiGoals);
  const rphGoal = Number(s.rphGoal) > 0 ? Number(s.rphGoal) : 0;
  const derived = useDerivedRevenueMetrics(derivedIds, displayWin, displayCustom, reading, yetiByTeam);

  /* Fixed number of hook calls (React's rule against conditional hooks) —
     one useTotalRevenue per possible rollup row slot, null revKpi for any
     slot this card doesn't actually use (the hook already returns an
     inactive {amount:0} result for that case). */
  const rollupSlots = Array.from({ length: MAX_ROLLUP_SLOTS }, (_, i) => rollupRows[i] || null);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const rollupResults = rollupSlots.map((slot) => useTotalRevenue(
    slot ? { workspaceId: slot.workspaceId, kpiId: slot.kpiId } : null,
    displayWin,
    displayCustom,
  ));

  let kpis = ctx.kpisAll || ctx.kpis || [];
  let metrics = realMetricRows;

  /* Everything below resolves REAL metric rows only — a card made up entirely
     of derived rows has nothing for any of these branches to find, and must
     not fall through to whatever ctx/data happens to hold (that showed a
     stray unrelated metric instead of nothing/derived-only). */
  if (realMetricRows.length || (scoped && hasMetricPlan(plan))) {
    if (scoped && hasMetricPlan(plan)) {
      if ((data.kpis || []).length) {
        kpis = data.kpis;
        metrics = data.kpis.map((col, i) => ({
          metricId: String(col._id),
          graph: (metricRows[i] && metricRows[i].graph)
            || (metricRows[0] && metricRows[0].graph)
            || "number",
          color: (metricRows[i] && metricRows[i].color) || null,
        }));
      }
    } else if (scoped && realMetricRows.length) {
      /* Prefer scoped window amounts over month-bucket ctx.kpis so Average Ticket
         (and every other rate) matches Beat the Yeti for the same date filter. */
      const pool = [...(data.kpis || []), ...(data.kpisAll || [])];
      const matchedKpis = realMetricRows
        .map((row) => resolveRowKpi(row, pool, companyKpis, reading))
        .filter(Boolean);
      if (matchedKpis.length) {
        kpis = matchedKpis;
        metrics = realMetricRows;
      } else if ((data.kpis || []).length) {
        /* Named row didn't resolve — still show scoped live tiles, not month ctx. */
        kpis = data.kpis;
      }
    } else if (scoped && (data.kpis || []).length) {
      kpis = data.kpis;
      metrics = data.kpis.map((col) => ({
        metricId: String(col._id),
        graph: (realMetricRows[0] && realMetricRows[0].graph) || "number",
        color: null,
      }));
    } else if (!scoped && realMetricRows.length) {
      const pool = [...(ctx.kpisAll || []), ...(ctx.kpis || [])];
      const matchedKpis = realMetricRows
        .map((row) => resolveRowKpi(row, pool, companyKpis, reading))
        .filter(Boolean);
      if (matchedKpis.length) {
        kpis = matchedKpis;
        metrics = realMetricRows;
      }
    }
  }

  /* Answer derived rows here, independent of every branch above — they read
     the company's canonical Total Revenue KPI (and, for the per-hour one,
     Technicians' Hours Worked), never the card's own scoped workspace data. */
  /* One tile object per derived metric, whichever list it goes into. */
  const derivedKpiOf = (id) => {
    const d = derived[id];
    if (!d) return null;
    /* Technicians' Revenue per Hour has no goal of its own — it borrows a twin
       anchor metric's (see helpers/derivedGoal), which an admin can neither see
       nor change from here. A goal typed on the card wins; it is a whole-period
       $/hr figure, so it is marked fixed and never re-scaled by the window. */
    const ownGoal = id === TECH_REVENUE_PER_HOUR_ID && rphGoal > 0;
    const g = ownGoal ? { target: rphGoal, metricType: "anchor", yeti: d.goal ? d.goal.yeti : null } : d.goal;
    return {
      _id: id,
      title: d.title,
      kind: d.kind,
      amount: d.amount,
      target: g ? g.target : 0,
      points: 0,
      _derived: true,
      /* Beat the Yeti rate only (see hooks/useDerivedRevenueMetrics). */
      /* Each team is marked with its own goal (else the overall one). */
      ...(d.teams && d.teams.length && {
        _yetiTeams: d.teams.map((t) => ({ ...t, goal: yetiGoalFor(yetiGoals, yetiGoal, t.workspaceId) })),
      }),
      ...(id === BEAT_YETI_RATE_ID && {
        _yetiBar: yetiBar,
        _yetiGoal: yetiGoal,
        _yetiCount: { beating: d.beating || 0, players: d.players || 0 },
      }),
      ...(g && { metricType: g.metricType, _dailyTarget: g.target, _derivedYeti: g.yeti }),
      ...(ownGoal && { _fixedTarget: true }),
    };
  };

  if (derivedRows.length) {
    const already = new Set(kpis.map((k) => String(k?._id)));
    derivedRows.forEach((row) => {
      const id = String(row.metricId);
      if (already.has(id)) return;
      const tile = derivedKpiOf(id);
      if (!tile) return;
      /* A derived metric has no target of its own; when a real anchor metric
         measures the same thing, its goal (and the Yeti's pace on it) is
         lent to the tile — see helpers/derivedGoal. Marked anchor so the
         goal reads as the whole-period figure it is, not a per-day one to be
         multiplied by the days elapsed. */
      kpis = [...kpis, tile];
      already.add(id);
    });
    const haveMetricIds = new Set(metrics.map((m) => String(m.metricId)));
    const missingRows = derivedRows.filter((r) => !haveMetricIds.has(String(r.metricId)));
    if (missingRows.length) metrics = [...metrics, ...missingRows];
  }

  /* Answer rollup rows (Total Revenue / Total Scheduled Revenue) the same
     dedicated way — see isRollupKpi's comment for why they can never
     resolve through the branches above. */
  if (rollupRows.length) {
    const already = new Set(kpis.map((k) => String(k?._id)));
    rollupRows.forEach((entry, i) => {
      const id = String(entry.row.metricId);
      if (already.has(id)) return;
      const d = rollupResults[i];
      if (!d || !d.active) return;
      kpis = [...kpis, {
        _id: id, title: id, kind: "$", amount: d.amount, target: 0, points: 0, _derived: true,
      }];
      already.add(id);
    });
    const haveMetricIds2 = new Set(metrics.map((m) => String(m.metricId)));
    const missingRollupRows = rollupRows.map((r) => r.row).filter((r) => !haveMetricIds2.has(String(r.metricId)));
    if (missingRollupRows.length) metrics = [...metrics, ...missingRollupRows];
  }

  /* A derived tile carries its own day-by-day numbers (see
     useDerivedRevenueMetrics) — without this, Line/Area/Bar/Spark all read
     as blank no matter which one is picked, since nothing in seriesByDay
     was ever keyed by a synthetic id. */
  const derivedSeriesByDay = useMemo(() => {
    if (!derivedIds.length) return null;
    const out = {};
    derivedIds.forEach((id) => {
      (derived[id]?.dailySeries || []).forEach((pt) => {
        if (!out[pt.date]) out[pt.date] = {};
        out[pt.date][id] = { amt: pt.value };
      });
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [derivedIds.join(","), derived]);

  /* Same fix, for rollup rows — useTotalRevenue's own dailySeries (real
     day-by-day revenue for that KPI) keyed onto the row's synthetic id. */
  const rollupSeriesByDay = useMemo(() => {
    if (!rollupRows.length) return null;
    const out = {};
    rollupRows.forEach((entry, i) => {
      const id = String(entry.row.metricId);
      (rollupResults[i]?.dailySeries || []).forEach((pt) => {
        if (!out[pt.date]) out[pt.date] = {};
        out[pt.date][id] = { amt: pt.value };
      });
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rollupRows.map((r) => r.row.metricId).join(","), rollupResults]);

  if (scoped && metrics.length === 1 && kpis.length === 1) {
    const only = kpis[0];
    if (only && String(only._id) !== String(metrics[0].metricId)) {
      kpis = [{ ...only, _id: String(metrics[0].metricId) }];
    }
  }

  /* The bonus-only derived metric joins the pool AFTER the remap above — one
     extra entry there would have stopped a lone scoped tile being matched to
     its row. It is in `kpis` (so the bonus can read it) but not in `metrics`
     (so it never draws a tile). */
  if (bonusOnlyDerivedId && !kpis.some((k) => String(k?._id) === bonusOnlyDerivedId)) {
    const tile = derivedKpiOf(bonusOnlyDerivedId);
    if (tile) kpis = [...kpis, tile];
  }

  /* Stamp the Scorekeeper daily goal on every tile. Scaling for Today / This
     year / etc. happens in CustomWidget from `_dailyTarget` + period — that is
     the only place that paints "of X", so Memberships cannot stay stuck at 5. */
  if (kpis.length) {
    kpis = kpis.map((k) => {
      if (!k) return k;
      /* Team total: a per-rep goal set against a sum over every rep would show
         a 5-rep team at "206% of goal". Scale the goal to the reps the total is
         the sum of. (A goal typed on the card itself is applied below and is
         already a team figure, so it overrides this.) */
      const teamSize = s.avgMode === "total" ? teamSizeOf(k) : 0;
      const daily = rawTargetOf(k) * (teamSize > 0 ? teamSize : 1);
      /* The Yeti's pace for this tile, read off the same per-workspace Yeti the
         Beat the Yeti card uses (null = nothing honest to compare with). */
      const yeti = k._derived
        ? (k._derivedYeti || null)
        : yetiPaceFor(k, {
          yetiByWs: data.yetiPaceByWs,
          yetiWin: data.yetiWin,
          window: displayWin,
          workspaceIds: reading,
          show: s.avgMode === "total" ? "total" : "avg",
        });
      return { ...k, _dailyTarget: daily, target: daily, _yeti: yeti };
    });
  }

  const goalTarget = Number(s.goalTarget) || 0;
  if (goalTarget > 0 && kpis.length) {
    kpis = kpis.map((k) => (
      k ? { ...k, target: goalTarget, _dailyTarget: goalTarget, _fixedTarget: true } : k
    ));
  }

  /* Gates read metrics the card is NOT paired with — "close rate" can gate a
     revenue curve — so they need the whole live pool, not just `kpis`. Scoped
     amounts come first so a gate reads the same window the board does. Each
     raw definition is tagged with the workspace it came from (`_wsId`) so a
     gate can pool same-named metrics across ONLY the card's own assigned
     workspaces (see readMetric in components/CustomWidget/index.jsx) instead
     of averaging in every other workspace in the company that happens to
     share the name. */
  const gateKpis = useMemo(() => {
    /* companyKpis is every KPI DEFINITION in the company, keyed by workspace —
       the only pool that holds a metric the card is not itself paired with,
       which is the normal case for a gate. Live scoped amounts come first so a
       definition's stale figure never wins over a real reading. */
    const defs = Object.entries(companyKpis || {}).flatMap(([wsId, list]) =>
      (list || []).filter(Boolean).map((k) => ({ ...k, _wsId: String(wsId) })));
    return [...(data.kpis || []), ...(data.kpisAll || []), ...(ctx.kpisAll || []), ...(ctx.kpis || []), ...defs];
  }, [data.kpis, data.kpisAll, ctx.kpisAll, ctx.kpis, companyKpis]);

  const { who, isRealAdmin, metrics: _m, showOn: _s, ...rest } = widget;
  const canSwitch = !!isRealAdmin && who !== false && !scoped;
  const periodProp = displayWin === "year" ? "ytd" : displayWin;
  const driveId = metrics[0] ? String(metrics[0].metricId) : null;
  const coldLoad = !!(data.loading || (hasMetricPlan(plan) && !data.kpis?.length && reading.length));
  const softLoad = !!data.refreshing;

  const baseSeriesByDay = rest.seriesByDay || data.seriesByDay || null;
  const seriesByDay = useMemo(() => {
    const overlays = [derivedSeriesByDay, rollupSeriesByDay].filter(Boolean);
    if (!overlays.length) return baseSeriesByDay;
    const merged = { ...(baseSeriesByDay || {}) };
    overlays.forEach((overlay) => {
      Object.entries(overlay).forEach(([day, cells]) => {
        merged[day] = { ...(merged[day] || {}), ...cells };
      });
    });
    return merged;
  }, [baseSeriesByDay, derivedSeriesByDay, rollupSeriesByDay]);

  return (
    <div className={`cs-wrap${softLoad ? " cs-is-refreshing" : ""}`} style={{ position: "relative" }}>
      <CardBusy
        active={coldLoad || softLoad}
        cover={coldLoad}
        label={coldLoad ? "Loading metrics…" : "Updating for date range…"}
      />
      <CustomWidget
        {...rest}
        metrics={metrics}
        kpis={kpis}
        seriesByDay={seriesByDay}
        canSwitch={canSwitch}
        loading={coldLoad}
        refreshing={softLoad}
        period={periodProp}
        customRange={displayCustom}
        showOn={driveId}
        gateKpis={gateKpis}
        gateWorkspaceIds={effectiveWs}
      />
    </div>
  );
};

ScopedCustomWidget.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  ctx: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  s: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  widget: PropTypes.object.isRequired,
};

export default ScopedCustomWidget;
